// Auth Context - handles user authentication state
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';

const OAUTH_POST_LOGIN_PATH_KEY = 'oauth-post-login-path';
const OAUTH_STARTED_AT_KEY = 'oauth-started-at';
const OAUTH_MAX_AGE_MS = 5 * 60 * 1000;

const getOAuthUrlParams = () => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(window.location.search);
  return { hashParams, searchParams };
};

const hasOAuthRedirectParams = () => {
  const { hashParams, searchParams } = getOAuthUrlParams();
  return Boolean(
    hashParams.get('access_token') ||
    hashParams.get('refresh_token') ||
    searchParams.get('access_token') ||
    searchParams.get('refresh_token') ||
    (searchParams.get('code') && searchParams.get('state'))
  );
};

const cleanOAuthParamsFromUrl = () => {
  const url = new URL(window.location.href);
  ['code', 'state', 'error', 'error_description', 'access_token', 'refresh_token', 'expires_in', 'expires_at', 'token_type', 'type'].forEach((key) => {
    url.searchParams.delete(key);
  });
  url.hash = '';
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
};

const consumeOAuthRedirectSession = async () => {
  const { hashParams, searchParams } = getOAuthUrlParams();
  const accessToken = hashParams.get('access_token') ?? searchParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token') ?? searchParams.get('refresh_token');

  if (accessToken && refreshToken) {
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) throw error;
    cleanOAuthParamsFromUrl();
    return data.session;
  }

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const startedAt = Number(sessionStorage.getItem(OAUTH_STARTED_AT_KEY) ?? 0);
  const isRecentOAuthReturn = Boolean(state && startedAt && Date.now() - startedAt < OAUTH_MAX_AGE_MS);

  if (code && isRecentOAuthReturn) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    cleanOAuthParamsFromUrl();
    return data.session;
  }

  return null;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string, phone?: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let resolvingOAuthRedirect = hasOAuthRedirectParams();

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (resolvingOAuthRedirect && event === 'INITIAL_SESSION' && !session) {
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          sessionStorage.removeItem(OAUTH_POST_LOGIN_PATH_KEY);
          sessionStorage.removeItem(OAUTH_STARTED_AT_KEY);
        }

        // Create trial subscription for new users via Edge Function (server-side)
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(async () => {
            try {
              const { data, error } = await supabase.functions.invoke('create-trial-subscription', {
                headers: {
                  Authorization: `Bearer ${session.access_token}`,
                },
              });
              
              // Only log actual errors, not skipped cases
              if (error) {
                // Handle 401/JWT/session errors - try refreshing session
                const msg = error.message || '';
                const lower = msg.toLowerCase();
                const isAuthError =
                  msg.includes('401') ||
                  lower.includes('jwt') ||
                  lower.includes('unauthorized') ||
                  lower.includes('session from session_id claim') ||
                  (lower.includes('session_id') && lower.includes('does not exist'));

                if (isAuthError) {
                  const { error: refreshError } = await supabase.auth.refreshSession();
                  if (refreshError) {
                    // Force re-login to stop loops with an invalid session
                    await supabase.auth.signOut();
                  }
                } else {
                  console.error('Error creating trial subscription:', error);
                }
              } else if (data?.skipped) {
                // Silently ignore skipped cases (super_admin, existing subscription)
              }
            } catch (err) {
              // Silently handle network errors to reduce console noise
            }
          }, 100);
        }
      }
    );

    // THEN check for existing session
    const initializeSession = async () => {
      try {
        const oauthSession = resolvingOAuthRedirect ? await consumeOAuthRedirectSession() : null;
        if (oauthSession) {
          setSession(oauthSession);
          setUser(oauthSession.user ?? null);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
      } finally {
        resolvingOAuthRedirect = false;
        setLoading(false);
      }
    };

    initializeSession();

    return () => subscription.unsubscribe();
  }, []);
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string, phone?: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || null,
          phone: phone || null,
        },
      },
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    sessionStorage.setItem(OAUTH_POST_LOGIN_PATH_KEY, '/dashboard');
    sessionStorage.setItem(OAUTH_STARTED_AT_KEY, String(Date.now()));

    const result = await lovable.auth.signInWithOAuth('google', {
      redirect_uri: `${window.location.origin}/dashboard`,
    });
    if (result.error) {
      return { error: result.error as unknown as Error };
    }
    return { error: null };
  };


  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  const signOut = async () => {
    // Try global sign out first; if the server session is already gone, fall back to local cleanup.
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) {
      await supabase.auth.signOut({ scope: 'local' });
    }

    // Extra safety: clear any cached auth tokens
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signInWithGoogle, resetPassword, updatePassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
